import {useState} from 'react';
import {Migrations} from '~/modules/api';
import {useCreateMigrationRunMutation} from '~/store/rtkQuery';
import {migrationTypeRegistry} from '../migrations.config';

export function CreateMigrationRunModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [selectedMigrationType, setSelectedMigrationType] =
    useState<Migrations.MigrationName>(
      Migrations.MigrationName.migrateDatoReferencesToCollection,
    );
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [paramValidationResults, setParamValidationResults] = useState<
    Record<
      string,
      {
        isValid: boolean;
        error?: string;
        parsed?: any;
        helperText?: string;
      }
    >
  >({});
  const [createMigrationRun, {isLoading: isCreating}] =
    useCreateMigrationRunMutation();

  // Helper: get param config for current type
  const paramDefs = migrationTypeRegistry[selectedMigrationType].params;

  // Helper: validate a single param
  function validateParam(paramKey: string, value: string) {
    const def = paramDefs[paramKey];
    const result = def.validate(value);
    setParamValidationResults(results => ({...results, [paramKey]: result}));
    return result;
  }

  // Helper: validate all params, return {valid, parsedParams}
  function validateAllParams() {
    let allValid = true;
    const parsedParams: Record<string, any> = {};
    Object.entries(paramDefs).forEach(([paramKey, def]) => {
      const value = paramValues[paramKey] || '';
      const result = def.validate(value);
      if (!result.isValid) {
        allValid = false;
      }
      setParamValidationResults(results => ({...results, [paramKey]: result}));
      if (result.parsed !== undefined) {
        parsedParams[paramKey] = result.parsed;
      }
    });
    return {allValid, parsedParams};
  }

  // On change for any param
  function handleParamChange(paramKey: string, value: string) {
    setParamValues(vals => ({...vals, [paramKey]: value}));
    validateParam(paramKey, value);
  }

  // On submit
  const handleCreateMigration = async () => {
    const {allValid, parsedParams} = validateAllParams();
    if (!allValid) return;
    try {
      await createMigrationRun({
        name: selectedMigrationType,
        params: parsedParams,
      });
      setParamValues({});
      setParamValidationResults({});
      onCreated();
    } catch (error) {
      // Optionally show error
    }
  };

  // Is form valid?
  const isValid = Object.entries(paramDefs).every(([paramKey, def], i) => {
    const results = paramValidationResults[paramKey];
    const isRequired = def.required;
    if (isRequired) {
      return results?.isValid;
    } else {
      if (results?.isValid === false) {
        return false;
      }
      return true;
    }
  });

  if (!open) return null;

  return (
    <dialog className="modal modal-open">
      <div className="modal-box">
        <form method="dialog">
          <button
            className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
            onClick={() => {
              setParamValues({});
              setParamValidationResults({});
              onClose();
            }}>
            ✕
          </button>
        </form>
        <h3 className="font-bold text-lg mb-4">Create New Migration Run</h3>
        <div className="form-control w-full mb-4">
          <label className="label">
            <span className="label-text">Migration Type</span>
          </label>
          <select
            className="select select-bordered w-full"
            value={selectedMigrationType}
            onChange={e => {
              setSelectedMigrationType(
                e.target.value as Migrations.MigrationName,
              );
              setParamValues({});
              setParamValidationResults({});
            }}>
            {Object.entries(migrationTypeRegistry).map(([key, def]) => (
              <option key={key} value={key}>
                {def.label}
              </option>
            ))}
          </select>
        </div>
        {Object.entries(paramDefs).map(([paramKey, def]) => (
          <div className="form-control w-full mb-4" key={paramKey}>
            <label className="label">
              <span className="label-text">
                {def.label}
                {def.required ? ' *' : ' (optional)'}
              </span>
            </label>
            {def.type === 'textarea' ? (
              <textarea
                className={`textarea textarea-bordered w-full ${paramValidationResults[paramKey]?.error ? 'textarea-error' : ''}`}
                placeholder={def.placeholder}
                value={paramValues[paramKey] || ''}
                onChange={e => handleParamChange(paramKey, e.target.value)}
                rows={6}
              />
            ) : def.type === 'checkbox' ? (
              <input
                className={`checkbox checkbox-bordered ${paramValidationResults[paramKey]?.error ? 'checkbox-error' : ''}`}
                type={def.type}
                value={paramValues[paramKey] || ''}
                onChange={e => handleParamChange(paramKey, e.target.value)}
              />
            ) : (
              <input
                className={`input input-bordered w-full ${paramValidationResults[paramKey]?.error ? 'input-error' : ''}`}
                type={def.type}
                placeholder={def.placeholder}
                value={paramValues[paramKey] || ''}
                onChange={e => handleParamChange(paramKey, e.target.value)}
              />
            )}
            {paramValidationResults[paramKey]?.error && (
              <label className="label">
                <span className="label-text-alt text-error font-medium">
                  {paramValidationResults[paramKey].error}
                </span>
              </label>
            )}
            {!!paramValidationResults[paramKey]?.helperText && (
              <label className="label">
                <span className="label-text-alt text-success font-medium">
                  {paramValidationResults[paramKey].helperText}
                </span>
              </label>
            )}
          </div>
        ))}
        <div className="modal-action">
          <button
            className="btn"
            onClick={() => {
              setParamValues({});
              setParamValidationResults({});
              onClose();
            }}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCreateMigration}
            disabled={isCreating || !isValid}>
            {isCreating ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Creating...
              </>
            ) : (
              'Create Migration'
            )}
          </button>
        </div>
      </div>
    </dialog>
  );
}
